import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { businessService } from '../../../services/api/businessService';
import {
  PROVIDER_HIGHLIGHTS_LEAD_IN,
  formatProviderHighlight,
  providerHighlightPath,
} from '../../../utils/providerHighlights';
import logoMark from '../../../assets/images/nky_connect_logo_only.png';
import {
  HighlightLink,
  LeadIn,
  SeparatorMark,
  TickerRegion,
  TickerSequence,
  TickerTrack,
} from './SiteTicker.styled';

const PIXELS_PER_SECOND = 32;

function HighlightSequence({ items, copy }) {
  const hidden = copy === 'duplicate';

  return (
    <TickerSequence data-copy={copy} aria-hidden={hidden || undefined}>
      <LeadIn>{PROVIDER_HIGHLIGHTS_LEAD_IN}</LeadIn>
      {items.map((item, index) => (
        <React.Fragment key={`${copy}-${item.id}`}>
          {index > 0 ? <SeparatorMark src={logoMark} alt="" /> : null}
          <HighlightLink
            as={Link}
            to={item.href}
            tabIndex={hidden ? -1 : 0}
          >
            {item.text}
          </HighlightLink>
        </React.Fragment>
      ))}
    </TickerSequence>
  );
}

export default function SiteTicker() {
  const [highlights, setHighlights] = useState(null);
  const [durationSec, setDurationSec] = useState(40);
  const trackRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    businessService
      .getProviderHighlights()
      .then((data) => {
        if (!cancelled) setHighlights(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setHighlights([]);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const items = useMemo(() => {
    return (highlights ?? [])
      .map((highlight) => ({
        id: highlight?.BusinessId || highlight?.BusinessSlug,
        text: formatProviderHighlight(highlight),
        href: providerHighlightPath(highlight),
      }))
      .filter((item) => item.id && item.text && item.href);
  }, [highlights]);

  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track || items.length === 0) return undefined;

    const updateDuration = () => {
      const distance = track.scrollWidth / 2;
      if (distance > 0) {
        setDurationSec(distance / PIXELS_PER_SECOND);
      }
    };

    updateDuration();

    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(updateDuration);
    observer.observe(track);
    return () => observer.disconnect();
  }, [items]);

  if (items.length === 0) return null;

  return (
    <TickerRegion role="region" aria-label="Featured local providers">
      <TickerTrack
        ref={trackRef}
        className="site-ticker-track"
        $duration={durationSec}
      >
        <HighlightSequence items={items} copy="primary" />
        <HighlightSequence items={items} copy="duplicate" />
      </TickerTrack>
    </TickerRegion>
  );
}
